<template>
    <div class="forum-category panel">
        <div
            class="panel-body"
            v-show="category">

            <header>
                <div class="flex">
                    <div class="flex-full">
                        <h1>{{ categoryLabel }}</h1>
                        <div
                            class="summary"
                            v-if="categorySummary">
                            | {{ categorySummary }}
                        </div>
                        <div class="links">
                            <router-link :to="homeUrl">
                                Home
                            </router-link>
                            <span class="breadcrumb-divider">/</span>
                            <router-link :to="allCategoriesUrl">
                                All Categories
                            </router-link>
                        </div>
                    </div>
                    <div class="action-bar flex-none">
                        <router-link :to="searchUrl">
                            <i class="fa fa-search"></i>
                        </router-link>
                    </div>
                </div>
                <!-- v-if is used instead of calling ParallaxArea.update() once
                     the category is loaded. This is because the ParallaxArea
                     calculates the inner height upon mount, but since the
                     elements are hidden, this calculation comes out to be zero.
                     -->
                <ParallaxArea v-if="category">
                    <img
                        :src="categoryBannerUrl"
                        v-if="categoryBannerUrl">
                    <img
                        src="../assets/img/ttto-thumbnail.jpg"
                        v-else>
                </ParallaxArea>
            </header>

            <div class="thread-controls flex">
                <div class="flex flex-full">
                    <TabSelect
                        :options="threadSubsetOptions"
                        :value="threadSubset"
                        @input="setThreadSubset($event)">
                    </TabSelect>
                    <DropdownSelect
                        :options="threadSubsetPeriodOptions"
                        :value="threadSubsetPeriod"
                        @input="setThreadSubsetPeriod($event)"
                        v-if="threadSubset === 'top'">
                    </DropdownSelect>
                </div>
                <div class="flex-none">
                    <router-link :to="newThreadUrl">
                        <i class="fa fa-plus"></i>
                        Create Thread
                    </router-link>
                </div>
            </div>

            <ForumThreadList
                ref="threadList"
                :sortable="true"
                :sortOrder="threadSortOrder"
                :threads="threads"
                @update:sort-order="threadSortOrder = $event">
                <template slot="extra" scope="{ thread }">
                    <div class="thread-author">
                        <router-link :to="getAuthorUrl(thread.author)">
                            <ForumAvatar :src="thread.author.avatarUrl">
                            </ForumAvatar>
                            <div class="thread-author-name">
                                {{ thread.author.name }}
                            </div>
                        </router-link>
                    </div>
                </template>
            </ForumThreadList>

            <Loader
                v-if="canLoadMoreThreads"
                v-show="loadingThreads">
            </Loader>

            <div
                class="after-threads-msg"
                v-if="!canLoadMoreThreads">
                That's all, folks!
            </div>
        </div>

        <div
            class="panel-body"
            v-if="loadingCategory">
            <Loader></Loader>
        </div>

        <div
            class="panel-body"
            v-if="categoryNotFound">
            <ForumCategoryNotFound></ForumCategoryNotFound>
        </div>
    </div>
</template>

<script>
    // ForumCategory: displays the category banner and an auto-loading list of
    // threads within the category.

    import DropdownSelect from './reusable/forum/dropdown-select';
    import ForumAvatar from './reusable/forum/avatar';
    import ForumCategoryNotFound from './reusable/forum/category-not-found';
    import ForumService from '../services/forum';
    import ForumThreadList from './reusable/forum/thread-list';
    import ForumUtils from 'src/utils/forum';
    import Loader from './reusable/forum/loader';
    import ParallaxArea from './reusable/parallax-area';
    import TabSelect from './reusable/forum/tab-select';


    function getViewportBottom() {
        const $window = $(window);
        return $window.scrollTop() + $window.height();
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }

    /**
     * Provides a paginated thread list where sticky threads are loaded in front
     * of non-sticky threads.
     */
    class PaginatedCategoryThreadList {
        constructor(params = {}) {
            this._stickyParams = Object.assign({}, params, { sticky: true });
            this._stickyThreads = null;
            this._stickyThreadsPromise = null;
            this._noStickyParams = Object.assign({}, params, { sticky: false });
            this._noStickyList = new ForumUtils.PaginatedThreadList(
                this._noStickyParams
            );
        }

        get threads() {
            if (this._stickyThreads) {
                const sticky = this._stickyThreads;
                const noSticky = this._noStickyList.threads;
                return [].concat(sticky, noSticky);
            } else {
                return [];
            }
        }

        get totalCount() {
            if (this._stickyThreads) {
                const sticky = this._stickyThreads.length;
                const noSticky = this._noStickyList.totalCount;
                return sticky + noSticky;
            } else {
                return [];
            }
        }

        isLoading() {
            const sticky = !!this.stickyThreadsPromise;
            const noSticky = this._noStickyList.isLoading();
            return sticky || noSticky;
        }

        hasMore() {
            if (this._stickyThreads) {
                return this._noStickyList.hasMore();
            } else {
                return true;
            }
        }

        async loadMore() {
            const promises = [];
            if (!this._stickyThreads) {
                promises.push(this._loadStickyThreads());
            }
            promises.push(this._noStickyList.loadMore());
            await Promise.all(promises);
        }

        async _loadStickyThreads() {
            const load = async () => {
                const resParams = this._stickyParams;
                const res = await ForumService.getThreadsAndCount(resParams);
                this._stickyThreads = res.threads;
            };

            try {
                this._stickyThreadsPromise = load();
                await this._stickyThreadsPromise;

            } finally {
                this._stickyThreadsPromise = null;
            }
        }
    }


    export default {
        props: {
            threadSubset: {
                type: String,
                default: 'latest',
                validator(value) {
                    return [
                        'latest',
                        'top',
                    ].includes(value);
                },
            },

            threadSubsetPeriod: {
                type: String,
                default: 'week',
                validator(value) {
                    return [
                        'week',
                        'month',
                        'semester',
                    ].includes(value);
                },
            },
        },

        components: {
            DropdownSelect,
            ForumAvatar,
            ForumCategoryNotFound,
            ForumThreadList,
            Loader,
            ParallaxArea,
            TabSelect,
        },

        data() {
            return {
                threadList: new PaginatedCategoryThreadList(),
                threadSortOrder: 'default',
                threadSubsetOptions: [
                    { value: 'latest', label: 'Latest' },
                    { value: 'top', label: 'Top' },
                ],
                threadSubsetPeriodOptions: [
                    { value: 'week', label: 'This week' },
                    { value: 'month', label: 'This month' },
                    { value: 'semester', label: 'This semester' },
                ],
                categoryLoadingPromise: null,
            };
        },

        async created() {
            // Get this started as soon as possible, since we'll need the
            // category id before we can start loading threads.
            await this.ensureCategory();
        },

        async mounted() {
            this.loadFiltersFromUrl();
            this.syncFiltersToUrl();
            window.addEventListener('scroll', this.onScroll);
            window.addEventListener('resize', this.onResize);

            // Reload the thread list rather than auto loading since the
            // original thread list is created without parameters.
            await this.reloadThreadList();
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onResize);
        },

        methods: {
            onScroll() {
                this.autoLoadThreads();
            },

            onResize() {
                this.autoLoadThreads();
            },

            loadFiltersFromUrl() {
                const query = this.$route.query;

                if (query['sort']) {
                    const value = query['sort'].toString().trim();
                    this.threadSortOrder = ForumUtils.sortOrderFromUrl(value);
                }
            },

            syncFiltersToUrl() {
                const setQueryParam = (name, value) => {
                    // Need to clone the current query in order for router to
                    // detect change
                    const query = Object.assign({}, this.$route.query);
                    if (value === null) {
                        delete query[name];
                    } else {
                        query[name] = value;
                    }
                    this.$router.replace({ query });
                }

                this.$watch('threadSortOrder', function (value) {
                    if (value !== 'default') {
                        setQueryParam('sort', ForumUtils.sortOrderToUrl(value));
                    } else {
                        setQueryParam('sort', null);
                    }
                });
            },

            async ensureCategory() {
                if (this.categoryLoadingPromise) {
                    return await this.categoryLoadingPromise;
                }
                try {
                    const p = this.$store.dispatch('ensureForumCategories');
                    this.categoryLoadingPromise = p;
                    await p;

                } finally {
                    this.categoryLoadingPromise = null;
                }
            },

            isThreadListBottomNearView() {
                const offset = 200;
                const viewBottom = getViewportBottom();
                const listBottom = getBottomPos(this.$refs.threadList.$el);
                return listBottom <= viewBottom + offset;
            },

            shouldLoadMoreThreads() {
                if (!this.canLoadMoreThreads) {
                    // There is nothing else to load.
                    return false;

                } else if (this.loadingThreads) {
                    // We are already loading the next batch.
                    return false;

                } else if (this.threads.length === 0) {
                    // If the thread list was just reloaded, the bottom may
                    // still be out of view due to the old rendered threads; in
                    // this case, we want to fetch the new ones.
                    return true;
                }
                return this.isThreadListBottomNearView();
            },

            async autoLoadThreads() {
                while (this.shouldLoadMoreThreads()) {
                    await this.threadList.loadMore();
                }
            },

            async reloadThreadList() {
                const params = this.threadSearchParams;
                this.threadList = new PaginatedCategoryThreadList(params);
                await this.autoLoadThreads();
            },

            /**
             * Copies $route object, changes the name, and pushes to $router.
             * Used by setThreadSubset() to copy any query parameters, etc.
             */
            changeRouteName(name) {
                const newRoute = Object.assign({}, this.$route);
                newRoute.path = null;
                newRoute.name = name;
                this.$router.push(newRoute);
            },

            setThreadSubset(subset) {
                switch (subset) {
                case 'latest':
                    this.changeRouteName('forum-category-latest');
                    break;
                case 'top':
                    this.changeRouteName('forum-category-top');
                    break;
                default:
                    throw new Error('unknown thread subset: ' + subset);
                }
            },

            setThreadSubsetPeriod(period) {
                switch (period) {
                case 'week':
                    this.changeRouteName('forum-category-top-week');
                    break;
                case 'month':
                    this.changeRouteName('forum-category-top-month');
                    break;
                case 'semester':
                    this.changeRouteName('forum-category-top-semester');
                    break;
                default:
                    throw new Error('unknown thread subset period: ' + period);
                }
            },

            getAuthorUrl(author) {
                if (author && author.id) {
                    return {
                        name: 'forum-user',
                        params: {
                            userId: author.id,
                        },
                    };
                } else {
                    return '';
                }
            },
        },

        computed: {
            threadSearchParams() {
                let order = this.threadSortOrder;
                let period;
                if (this.threadSubset === 'top') {
                    if (order === 'default') {
                        order = 'likeCount:desc';
                    }
                    period = this.threadSubsetPeriod;
                }
                return {
                    categoryId: this.categoryId,
                    order,
                    period,
                };
            },

            threads() {
                return this.threadList.threads;
            },

            allCategories() {
                return this.$store.getters.forumCategories;
            },

            categorySlug() {
                return this.$route.params.categorySlug;
            },

            category() {
                return this.allCategories.find(cat => {
                    return cat.name === this.categorySlug;
                });
            },

            categoryId() {
                return this.category ? this.category.id : null;
            },

            categoryLabel() {
                return this.category ? this.category.label : '';
            },

            categorySummary() {
                return this.category ? this.category.summary : null;
            },

            categoryBannerUrl() {
                // TODO: default URL
                const defaultUrl = '';
                return this.category ? this.category.bannerUrl : defaultUrl;
            },

            categoryFound() {
                return !!this.category;
            },

            categoryNotFound() {
                return !this.loadingCategory && !this.category;
            },

            canLoadMoreThreads() {
                return this.categoryFound && this.threadList.hasMore();
            },

            loadingCategory() {
                return !!this.categoryLoadingPromise;
            },

            loadingThreads() {
                return this.threadList.isLoading();
            },

            homeUrl() {
                return {
                    name: 'forum-home',
                };
            },

            allCategoriesUrl() {
                return {
                    name: 'forum-categories',
                };
            },

            searchUrl() {
                return {
                    name: 'forum-search',
                    query: {
                        category: this.categoryId,
                    },
                };
            },

            newThreadUrl() {
                return {
                    name: 'new-forum-thread',
                    query: {
                        category: this.categoryId,
                    },
                };
            },
        },

        watch: {
            threadSearchParams: function() {
                this.reloadThreadList();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex {
        display: flex;
    }
    .flex-full {
        flex: 1;
    }
    .flex-none {
        flex: 0;
    }

    .forum-category .panel-body {
        position: relative;
        margin: 20px 0;
        padding: 40px;
    }

    header {
        h1 {
            display: inline;
            margin: 0;
            font-size: 38px;
        }

        .summary {
            color: #777;
            display: inline;
            font-size: 22px;
            margin-left: 8px;
        }

        .links {
            margin-top: 10px;
            display: flex;

            a {
                color: #33ccff;
            }

            .breadcrumb-divider {
                margin: 0 7px;
            }
        }

        .action-bar {
            line-height: 40px;

            a {
                display: block;
                font-size: 24px;
                color: #b0b0b0;
                width: 40px;
                text-align: center;

                &:hover {
                    background: #363636;
                }

                i {
                    position: relative;
                    top: -2px;
                }
            }
        }

        .parallax-area {
            margin-top: 30px;
            position: relative;
            width: calc(100% + 40px * 2);
            left: -40px;
            height: 200px;

            img {
                background-color: #333;
                display: block;
                width: 100%;
                height: 500px;
                object-fit: cover;
                margin-top:60px;
            }
        }
    }

    .thread-controls {
        margin-top: 30px;

        a {
            background: #363636;
            border: none;
            color: #b0b0b0;
            display: inline-block;
            white-space: nowrap;
            height: 40px;
            line-height: 40px;
            padding: 0 12px;
            text-decoration: none;

            &:hover {
                background: #b0b0b0;
                color: #363636;
            }

            i {
                margin-right: 3px;
            }
        }

        .dropdown-select {
            //margin-top: 20px;
        }
    }

    .forum-thread-list {
        margin-top: 20px;

        .thread-author {
            margin-top: 5px;

            a {
                display: inline-flex;
                color: #b0b0b0;

                &:hover {
                    color: #33ccff;
                }
            }

            .forum-avatar {
                width: 20px;
                height: 20px;
                font-size: 18px;
            }

            .thread-author-name {
                margin-left: 5px;
            }
        }
    }

    .after-threads-msg {
        font-weight: bold;
        margin-top: 30px;
    }
</style>

<style lang="scss">
    .forum-category {
        .forum-dropdown-select.dropdown-select {
            margin-left: 20px;
        }
    }
</style>



// WEBPACK FOOTER //
// ForumCategory.vue?dd7941fc